import { CaseDictionaryService } from '../../helpers/classes/CaseDictionaryService';
import { ExceptionManager } from '../../helpers/classes/ExceptionManager';
import HttpService from '../../helpers/classes/HttpService';
import { ImageService } from '../../helpers/classes/ImageService';
import { ReductionService } from '../../helpers/classes/ReductionService';
import SocketService from '../../helpers/classes/SocketService';
import StorageService from '../../helpers/classes/StorageService';
import { dictionary } from '../configuration/CaseDictionary';
import { constants } from '../configuration/Constants';
import { ENDPOINTS } from '../configuration/Endpoints';
import { STORAGE_KEYS } from '../configuration/StorageKeys';
import { ResponseType } from '../enums/ResponseType';
import { TranslationKey } from '../enums/Translation';
import { IAuthSettings } from '../interfaces/AuthSettings';
import { IConfiguration } from '../interfaces/Configuration';
import { IEndpoints } from '../interfaces/Endpoints';
import { IFinalResult } from '../interfaces/FinalResult';
import { IJourneyDefinition } from '../interfaces/JourneyDefinition';
import { ILoginOptions } from '../interfaces/LoginOptions';
import { IUserCredentials } from '../interfaces/UserCredentials';
import { IUserInfo } from '../interfaces/UserInfo';
import { IVersionInfo } from '../interfaces/VersionInfo';
import { Journey } from './Journey';
import { IAdditionalData } from '../interfaces/AdditionalData';
import { Mode } from '../enums/Mode';

export class JourneyManager {

	public get journeys() {
		return this._journeys;
	}

	public get mode() {
		return this._mode;
	}

	private _case: CaseDictionaryService = new CaseDictionaryService(dictionary);
	private _journeys: Journey[] = [];
	private _endpoints?: IEndpoints;
	private _authSettings?: false | IAuthSettings;
	private _config?: IConfiguration;
	private _version?: IVersionInfo;
	private _mode?: Mode;
	private _isVerboseLoggingEnabled?: boolean;

	public configure = (config: IConfiguration): Promise<this> => {
		this._config = config;

		const { auth, backendUrl, endpoints, mode, isVerboseLogEnabled, hideLogo } = this._config;

		this._endpoints = endpoints ? endpoints : constants.ENDPOINTS;
		this._authSettings = auth !== true ? auth : constants.AUTH_DEFAULTS;
		this._mode = mode ? mode : Mode.WEB;
		this._isVerboseLoggingEnabled = isVerboseLogEnabled;

		return SocketService.configure(backendUrl + this._endpoints.socket)
			.then(() => this.assertBackendUrlExists(config))
			.then(() => this);
	}

	public initialize = ({ token }: { token?: string }): Promise<this> => {
		var startTime = performance.now();

		return HttpService.configure({
			authSettings: this._authSettings!,
			backendUrl: this._config!.backendUrl,
			endpoints: this._endpoints!,
			responseType: ResponseType.REFINED
		})
			.then(() => HttpService.initialize())
			.then(() => {
				if (token) {
					return this.assertAuthEnabled()
						.then(() => HttpService.injectToken(token))
						.then(() => this.setUserInfo());
				}

				var endTime = performance.now();
				if (this._isVerboseLoggingEnabled)
					console.log(`JourneyManager initialize method took: ${endTime - startTime} miliseconds`);
			})
			.then(() => this);
	}

	public caseDic = (res: any): any => this._case!.convert(res, 'beautify');

	/**
	 * Returns the version of the library and the backend
	 */
	public getVersion = (): Promise<IVersionInfo> => {
		if (this._version !== undefined) {
			return Promise.resolve(this._version);
		} else {
			return HttpService.get(this._endpoints!.getVersion + '?channel=' + this._mode, {}, 'version')
				.then(({ buildVersion, edition, CapturingMedia = 'FileUpload' }: any) => {
					return this._version = {
						backend: buildVersion,
						backendEdition: edition,
						capturingMedia: CapturingMedia,
						library: constants.VERSION,
					};
				});
		}
	}

	/**
	 * Injects a token to httpService, useful for dynamic token refresh
	 * @param token
	 */
	public injectToken = (token: string) => this.assertAuthEnabled()
		.then(() => HttpService.injectToken(token))

	/**
	 * Sets the userName and userGroups values in storage
	 */
	public setUserInfo = (): Promise<IUserInfo> => HttpService.post(ENDPOINTS.postToken)
		.then((res) => {
			if (STORAGE_KEYS.journeyId) { StorageService.set(STORAGE_KEYS.journeyId, res.journeyId, 24); }
			if (STORAGE_KEYS.journeyDefinitionId) { StorageService.set(STORAGE_KEYS.journeyDefinitionId, res.journeyDefinitionId, 24); }		
			return res;
		})
		.catch((e) => {
			throw new ExceptionManager.AuthorizationException('error while getting userInfo');
		})

	/**
	 * This is an example of a comment.
	 * @param user
	 */
	public login = (user: IUserCredentials): Promise<IUserInfo> => {
		user.area = user.area ? user.area : 'scanning';

		return this.assertUsernameExists(user)
			.then(() => this.assertPasswordExists(user))
			.then(() => this.doLogin(user))
			.then((res) => {
				const options: ILoginOptions = { fullName: false };
				const originalResponse = ReductionService.reduce(res, options);

				StorageService.set(STORAGE_KEYS.userName, user.username, 24);
				StorageService.set(STORAGE_KEYS.userGroups, user.memberOf, 24);
				StorageService.set(STORAGE_KEYS.journeyId, user.journeyId, 24);
				StorageService.set(STORAGE_KEYS.journeyDefinitionId, user.journeyDefinitionId, 24);
				return {
					fullName: originalResponse.fullName,
					username: originalResponse['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
					role: originalResponse['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
					token: originalResponse.access_token,
					memberOf: originalResponse.memberOf,
					journeyId: originalResponse.journeyId,
					journeyDefinitionId: originalResponse.journeyDefinitionId,
				};
			});
	}

	/**
	 * To logout from the server
	 */
	public logout = (): Promise<void> => this.assertUserAuthenticated()
		.then(() => this.doLogout())

	/**
	 * Creates a journey instance and returns the id of the instance.
	 * @param id
	 * @param additionalData
	 */
	public createJourney = (id?: string, additionalData: IAdditionalData[] = []): Promise<Journey> => this.assertUserAuthenticated()
		.then(() => {
			var startTime = performance.now();
			const journey = new Journey(additionalData, id);
			this._journeys.push(journey);

			var endTime = performance.now();
			if (this._isVerboseLoggingEnabled)
				console.log(`JourneyManager createJourney method took: ${endTime - startTime} miliseconds`);

			return journey;
		})
		.then((journey) => journey.start())

	/**
	 * Blob to string conversion
	 * @param blob
	 */
	public toBase64 = (blob: any): Promise<string> => ImageService.convert(blob);

	public getToken = () => StorageService.get(STORAGE_KEYS.defaultToken);

	/**
	 * Retrieve result of journey FUNCTION
	 *
	 * @param journeyId
	 */
	public getResults = (journeyId: string): Promise<IFinalResult> => {
		return this.assertUserAuthenticated()
			.then(() => {
				var startTime = performance.now();
				const headersRequest = {
					'journeyId': `${journeyId}`,
				};
				var journeyResult = HttpService.get(`${this._endpoints!.getJourney}`, {headers :headersRequest}, 'getJourney');

				var endTime = performance.now();
				if (this._isVerboseLoggingEnabled)
					console.log(`JourneyManager getResults method took: ${endTime - startTime} miliseconds`);

				return journeyResult;
			})
			.catch((e) => {
				if (e.response && e.response.status === 401) {
					return ExceptionManager.process(new ExceptionManager.AuthorizationException(TranslationKey.EXCEPTION_AUTHORIZATION_ACCOUNT_NOT_AUTHORIZED, e.response));
				} else {
					return ExceptionManager.process(new ExceptionManager.ServerException(TranslationKey.EXCEPTION, e.response));
				}
			});
	}

	public getJourneyDefinitions = (): Promise<IJourneyDefinition[]> => {
		return HttpService.get(ENDPOINTS.journeyDefinitions + '?channelType=' + this._mode)
			.then((res: Record<string, IJourneyDefinition>) => Object.keys(res).map((key) => res[key]));
	}

	public getCapturingMedia = (journeyDefinitionId: string): Promise<string> => {
		return HttpService.get(ENDPOINTS.getCapturingMedia + '?channelType=' + this._mode + '&journeyDefinitionId=' + journeyDefinitionId)
			.then((res: any) => res);
	}

	private doLogin = (user: IUserCredentials) => HttpService.post(
		this._endpoints!.login,
		`userName=${encodeURIComponent(user.username)}&password=${encodeURIComponent(user.password)}&area=${encodeURIComponent(user.area!)}&login-form-type=pwd&grant_type=password`,
		{
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			}
		},
		'login'
	).catch((e) => {

		if (e.response === undefined) {
			throw ExceptionManager.process(new ExceptionManager.Exception(TranslationKey.EXCEPTION_SERVER_UNREACHABLE, e.response));
		}
		const { response: { status } } = e || { response: { status: 0 } } as any;

		switch (status) {
			case 400:
			case 401:
				throw ExceptionManager.process(new ExceptionManager.AuthorizationException(TranslationKey.EXCEPTION_AUTHORIZATION_AUTH_DETAILS_INVALID, e.response));
			case 500:
				throw ExceptionManager.process(new ExceptionManager.ServerException(TranslationKey.EXCEPTION_SERVER, e.response));
			default:
				throw ExceptionManager.process(new ExceptionManager.Exception(TranslationKey.EXCEPTION, e.response));
		}
	})

	private doLogout = () => HttpService.post(this._endpoints!.logout, undefined, undefined, 'logout').catch((e) => {
		const { response: { status } } = e || { response: { status: 0 } } as any;

		switch (status) {
			default:
				throw ExceptionManager.process(new ExceptionManager.Exception(TranslationKey.EXCEPTION, e.response));
		}
	})

	/**
	 * Assert if user is authenticated for this process.
	 */
	private assertUserAuthenticated = (): Promise<void> => new Promise((resolve, reject) => {
		if (HttpService.auth !== true && this._authSettings !== false) {
			return reject(ExceptionManager.process(new ExceptionManager.AuthorizationException(TranslationKey.EXCEPTION_AUTHORIZATION_PROCESS_NOT_AUTHORIZED)));
		} else {
			return resolve();
		}
	})

	/**
	 * Assert if user is authenticated for this process.
	 */
	private assertAuthEnabled = (): Promise<void> => new Promise((resolve, reject) => {
		if (this._authSettings === false) {
			return reject(ExceptionManager.process(new ExceptionManager.AuthorizationException(TranslationKey.EXCEPTION_AUTHORIZATION_AUTH_NOT_ENABLED)));
		} else {
			return resolve();
		}
	})

	/**
	 * Assert if Configuration include a backendUrl parameter.
	 * @param config
	 */
	private assertBackendUrlExists = ({ backendUrl }: IConfiguration): Promise<void> => new Promise((resolve, reject) => {
		if (!backendUrl) {
			return reject(ExceptionManager.process(new ExceptionManager.MissingParameterException(TranslationKey.EXCEPTION_MISSING_PARAMETER_BACKEND_URL_MISSING)));
		} else {
			return resolve();
		}
	})

	/**
	 * Assert if the Supplied parameters contain username.
	 * @param user
	 */
	private assertUsernameExists = (user: IUserCredentials): Promise<void> => new Promise((resolve, reject) => {
		if (!user.username) {
			return reject(ExceptionManager.process(new ExceptionManager.MissingParameterException(TranslationKey.EXCEPTION_MISSING_PARAMETER_USERNAME_MISSING)));
		} else {
			return resolve();
		}
	})

	/**
	 * Assert if the Supplied parameters contain password.
	 * @param user
	 */
	private assertPasswordExists = (user: IUserCredentials): Promise<void> => new Promise((resolve, reject) => {
		if (!user.password) {
			return reject(ExceptionManager.process(new ExceptionManager.MissingParameterException(TranslationKey.EXCEPTION_MISSING_PARAMETER_PASSWORD_MISSING)));
		} else {
			return resolve();
		}
	})
}

export default new JourneyManager();
